import { useEffect, useRef, useState } from "react";
import { KTIcon } from "../../../../_metronic/helpers";
import { useQuery } from "react-query";
import { attendeeSchema } from "./TicketInSpecialValidationSchema";
import { getTicketCurrentDetailedSpecial } from "../../../api/match/matchApi";
import { getOrganizationSpecial } from "../../../api/organization/organizationApi";

interface Organization {
  id: number;
  title: string;
}

interface Attendee {
  name: string;
  mobile: string;
  national_code: string;
  male: boolean;
  used_count: string;
}

const TicketInSpecialForm = ({ formik, labels, loading }) => {
  const [isMale, setIsMale] = useState<boolean>(true);
  const [attendees, setAttendees] = useState<Attendee[]>([
    {
      name: "",
      mobile: "",
      national_code: "",
      male: true,
      used_count: "1",
    },
  ]);
  const genderOptions = [
    { id: "female", name: "زن" },
    { id: "male", name: "مرد" },
  ];

  const { 
    data: tickets, 
    refetch: refetchTickets, 
    isLoading: ticketsLoading,
    isError: ticketsError 
  } = useQuery(
    ["getTicketCurrentDetailedSpecial", isMale], 
    () => getTicketCurrentDetailedSpecial(isMale),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      staleTime: 60000,
      retry: 1,
      enabled: formik.values.male !== undefined,
      onSuccess: (data) => {
        if (data?.data?.profile?.profilePlatforms?.length > 0) {
          formik.setFieldValue(
            "profilePlatformId",
            data.data.profile.profilePlatforms[0]?.id
          );
          formik.setFieldValue("matchId", data.data.id);
        }
      },
      onError: (error) => {
        console.error("Error fetching tickets:", error);
      }
    }
  );
  
  const availablePlatforms = tickets?.data?.profile?.profilePlatforms || [];
  const hasAvailablePlatforms = availablePlatforms.length > 0;

  useEffect(() => {
    const attendeesArray = Array.isArray(attendees) ? attendees : [attendees];
    formik.setFieldValue("attendees", attendeesArray);
  }, [attendees]);
  
  const { 
    data: organizations = [], 
    isLoading: organizationsLoading,
    isError: organizationsError 
  } = useQuery<Organization[]>(
    "organizations",
    getOrganizationSpecial,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      staleTime: 60000,
      retry: 1,
      onError: (error) => {
        console.error("Error fetching organizations:", error);
      }
    }
  );

  const addAttendee = () => {
    if (attendees.length < 5) {
      setAttendees((prev) => [
        ...prev,
        {
          name: "",
          mobile: "",
          national_code: "",
          male: isMale,
          used_count: "1",
        },
      ]);
    }
  };

  const removeAttendee = () => {
    if (attendees.length > 1) {
      setAttendees((prev) => prev.slice(0, -1));
    }
  };

  const checkErrors = () => {
    return Object.keys(formik.errors).length > 0 || !hasAvailablePlatforms;
  };

  const handleInputChange = (index: number, field: string, value: string) => {
    const updatedAttendees = [...attendees];
    updatedAttendees[index] = {
      ...updatedAttendees[index],
      [field]: value
    };
    setAttendees(updatedAttendees);
    
    formik.setFieldValue("attendees", updatedAttendees);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      formik.setFieldValue("file", file);
    }
  };

  const addAttendeeRef = useRef(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    formik.validateForm();
    if (Object.keys(formik.errors).length > 0) {
      return;
    }

    await formik.handleSubmit();
  };

  const handleGenderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const isMaleValue = e.target.value === "male";
    setIsMale(isMaleValue);
    
    // Reset platform selection when gender changes
    formik.setFieldValue("profilePlatformId", "");
    formik.setFieldValue("male", isMaleValue);
    
    // Update attendees gender
    const updatedAttendees = attendees.map(attendee => ({
      ...attendee,
      male: isMaleValue
    }));
    setAttendees(updatedAttendees);
    
    // Trigger refetch with the new gender value
    setTimeout(() => {
      refetchTickets();
    }, 100);
  };

  return (
    <>
      <div className="card-body">
        <div className="form-label fw-bold">اضافه / حذف کردن هوادار</div>
        <div
          className="relative w-50 border"
          style={{
            height: "50px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            ref={addAttendeeRef}
            className="absolute btn btn-primary"
            onClick={addAttendee}
            type="button"
          >
            +
          </button>
          <span>{attendees.length}</span>
          <button 
            className="absolute btn btn-primary" 
            onClick={removeAttendee}
            type="button"
          >
            -
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="form">
        <div className="card-body">
          <div className="row row-gap-10 mb-6">
            <div className="col-12 col-lg-6">
              <label htmlFor="maleSelect" className="form-label fw-bold">
                جنسیت
              </label>
              <select
                id="maleSelect"
                name="male"
                className={`form-select ${
                  formik.touched.male && formik.errors.male ? 'is-invalid' : ''
                }`}
                value={formik.values.male === true ? 'male' : formik.values.male === false ? 'female' : ''}
                onChange={handleGenderChange}
                onBlur={formik.handleBlur}
              >
                <option value="">انتخاب جنسیت</option>
                {genderOptions.map((option, index) => (
                  <option
                    value={option.id}
                    key={index}
                  >
                    {option.name}
                  </option>
                ))}
              </select>
              {formik.touched.male && formik.errors.male && (
                <div className="invalid-feedback">
                  {formik.errors.male}
                </div>
              )}
            </div>

            <div className="col-12 col-lg-6">
              <label htmlFor="operatorSelect" className="form-label fw-bold">
                انتخاب سازمان
              </label>
              <select
                id="operatorSelect"
                name="operatorId"
                className={`form-select ${
                  formik.touched.operatorId && formik.errors.operatorId ? 'is-invalid' : ''
                }`}
                value={formik.values.operatorId || ''}
                onChange={(e) => {
                  formik.setFieldValue("operatorId", e.target.value ? Number(e.target.value) : '');
                }}
                onBlur={formik.handleBlur}
              >
                <option value="">انتخاب سازمان</option>
                {organizationsLoading ? (
                  <option value="" disabled>در حال بارگذاری...</option>
                ) : (
                  organizations.map((org, index) => (
                    <option
                      value={org.id}
                      key={index}
                    >
                      {org.title}
                    </option>
                  ))
                )}
              </select>
              {formik.touched.operatorId && formik.errors.operatorId && (
                <div className="invalid-feedback">
                  {formik.errors.operatorId}
                </div>
              )}
            </div>
          </div>

          <div className="row row-gap-10 mb-6">
            {/* Platform selection section */}
            <div className="col-12 col-lg-6">
              {formik.values.male === undefined ? (
                <div className="alert alert-warning">
                  لطفا ابتدا جنسیت را انتخاب کنید
                </div>
              ) : ticketsLoading ? (
                <div className="d-flex align-items-center">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  <span>در حال بارگذاری جایگاه‌ها...</span>
                </div>
              ) : ticketsError ? (
                <div className="alert alert-danger">
                  خطا در بارگذاری جایگاه‌ها. لطفا مجددا تلاش کنید.
                </div>
              ) : hasAvailablePlatforms ? (
                <div>
                  <label htmlFor="platformSelect" className="form-label fw-bold">
                    انتخاب جایگاه
                  </label>
                  <select
                    id="platformSelect"
                    className="form-select"
                    aria-label="انتخاب جایگاه"
                    onChange={(e) => {
                      formik.setFieldValue(
                        "profilePlatformId",
                        Number(e.target.value)
                      );
                    }}
                    value={formik.values.profilePlatformId || ''}
                  >
                    <option value="">انتخاب جایگاه</option>
                    {availablePlatforms.map((platform, index) => (
                      <option
                        value={platform.id}
                        key={index}
                      >
                        {platform?.platform?.name} (ظرفیت: {platform?.remainedCapacity} نفر)
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="alert alert-info">
                  جایگاهی برای جنسیت انتخاب شده یافت نشد
                </div>
              )}
            </div>
          </div>
          
          {/* Attendees section */}
          {attendees.map((attendee, index) => (
            <div className="row mb-5 w-full flex-wrap" key={index}>
              <div className="col-lg-4 mb-1">
                <label className="form-label fw-bold">نام و نام خانوادگی</label>
                <input
                  className={`form-control form-control-lg form-control-solid ${
                    formik.touched.attendees?.[index]?.name && 
                    formik.errors.attendees?.[index]?.name ? 
                    'is-invalid' : ''
                  }`}
                  value={attendee.name}
                  onChange={(e) =>
                    handleInputChange(index, "name", e.target.value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched(`attendees[${index}].name`, true)
                  }
                />
                {formik.touched.attendees?.[index]?.name &&
                  formik.errors.attendees?.[index]?.name && (
                    <div className="invalid-feedback">
                      {formik.errors.attendees[index].name}
                    </div>
                  )}
              </div>

              <div className="col-lg-4 mb-1">
                <label className="form-label fw-bold">کد ملی</label>
                <input
                  className={`form-control form-control-lg form-control-solid ${
                    formik.touched.attendees?.[index]?.national_code && 
                    formik.errors.attendees?.[index]?.national_code ? 
                    'is-invalid' : ''
                  }`}
                  value={attendee.national_code}
                  onChange={(e) =>
                    handleInputChange(index, "national_code", e.target.value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched(
                      `attendees[${index}].national_code`,
                      true
                    )
                  }
                />
                {formik.touched.attendees?.[index]?.national_code &&
                  formik.errors.attendees?.[index]?.national_code && (
                    <div className="invalid-feedback">
                      {formik.errors.attendees[index].national_code}
                    </div>
                  )}
              </div>

              <div className="col-lg-4 mb-1">
                <label className="form-label fw-bold">شماره موبایل</label>
                <input
                  className={`form-control form-control-lg form-control-solid ${
                    formik.touched.attendees?.[index]?.mobile && 
                    formik.errors.attendees?.[index]?.mobile ? 
                    'is-invalid' : ''
                  }`}
                  value={attendee.mobile}
                  onChange={(e) =>
                    handleInputChange(index, "mobile", e.target.value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched(`attendees[${index}].mobile`, true)
                  }
                />
                {formik.touched.attendees?.[index]?.mobile &&
                  formik.errors.attendees?.[index]?.mobile && (
                    <div className="invalid-feedback">
                      {formik.errors.attendees[index].mobile}
                    </div>
                  )}
              </div>
              <div className="col-lg-4 mb-1">
                <label className="form-label fw-bold">تعداد تردد</label>
                <input
                  type="number"
                  min="1"
                  className={`form-control form-control-lg form-control-solid ${
                    formik.touched.attendees?.[index]?.used_count && 
                    formik.errors.attendees?.[index]?.used_count ? 
                    'is-invalid' : ''
                  }`}
                  value={attendee.used_count}
                  onChange={(e) =>
                    handleInputChange(index, "used_count", e.target.value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched(`attendees[${index}].used_count`, true)
                  }
                />
                {formik.touched.attendees?.[index]?.used_count &&
                  formik.errors.attendees?.[index]?.used_count && (
                    <div className="invalid-feedback">
                      {formik.errors.attendees[index].used_count}
                    </div>
                  )}
              </div>
            </div>
          ))}
          
          <div className="mt-4">
            <button
              type="submit"
              className="btn btn-primary d-flex align-items-center gap-2"
              disabled={loading || checkErrors()}
            >
              {loading ? "در حال ارسال..." : labels.sendBtnLabel}
              <span>
                <KTIcon iconName={"send"} className={"fs-2"} />
              </span>
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default TicketInSpecialForm;